import { Button, Card, Skeleton } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import React, { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { downloadCSV, formatDate, getUserRole } from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
import "./Details.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const DOWNLOAD_ROLES = ["Super Admin", "Admin", "Management"];

// ─── Column factory ───────────────────────────────────────────────────────────

const buildColumns = ({ componentLabel, sortedInfo, onHotelClick }) => {
    const sortOrder = (key) =>
        sortedInfo.columnKey === key ? sortedInfo.order : null;

    return [
        {
            title: "Hotel Name",
            dataIndex: "hotel_name",
            key: "hotel_name",
            fixed: "left",
            width: "28%",
            sorter: () => {},
            sortOrder: sortOrder("hotel_name"),
            render: (text, record) => (
                <span
                    className={record.count ? "details-link" : "details-plain"}
                    onClick={() =>
                        record.count && onHotelClick(record.hotel_id, record)
                    }
                >
                    {text}
                </span>
            ),
        },
        {
            title: componentLabel,
            dataIndex: "count",
            key: "count",
            sorter: () => {},
            sortOrder: sortOrder("count"),
            render: (val) => (
                <span
                    style={{ fontWeight: 600, color: "rgba(255,255,255,0.9)" }}
                >
                    {val}
                </span>
            ),
        },
        {
            title: "Valid Transactions",
            dataIndex: "valid_transactions",
            key: "valid_transactions",
            sorter: () => {},
            sortOrder: sortOrder("valid_transactions"),
        },
        {
            title: "Issue-to-Transaction Ratio",
            dataIndex: "ratio",
            key: "ratio",
            sorter: () => {},
            sortOrder: sortOrder("ratio"),
            render: (val) => (
                <span
                    style={{
                        color:
                            val > 1
                                ? "#ff6b6b"
                                : val > 0.5
                                ? "#ffd93d"
                                : "#6bcb77",
                        fontWeight: 600,
                    }}
                >
                    {val}
                </span>
            ),
        },
    ];
};

// ─── Component ────────────────────────────────────────────────────────────────

function QualityMatricsDetails() {
    const { component: type } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const userRole = getUserRole();
    const staff_id = localStorage.getItem("x-dashboard-staffid");

    const { selectedDates, selectedHotels, selectedHotelType, kioskSelection } =
        useContext(SettingContext);

    // ── State ─────────────────────────────────────────────────────────────────

    const [hotelData, setHotelData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortedInfo, updateSortedInfo] = useState({
        order: "descend",
        columnKey: "count",
    });

    const [start_date, end_date] = selectedDates;

    const [formData, setFormData] = useState({
        component_name: state?.component_name,
        hotel_ids: selectedHotels,
        all_selected: selectedHotels.length === 0,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        page: 0,
        limit: 10,
        hotel_type: selectedHotelType,
        ...(selectedHotelType === "kiosk"
            ? { calling_hotel_type: kioskSelection }
            : {}),
        staff_id,
        sort_by: "count",
        sorting: 1,
    });

    // ── Query ─────────────────────────────────────────────────────────────────

    const {
        status: detailStatus,
        data: detailRes,
        refetch: fetchDetails,
    } = useQuery(
        type,
        () => HTTP("/dashboard/getSupportTicketErrorDetails", { ...formData }),
        { enabled: false },
    );

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchDetails();
    }, []);
    useEffect(() => {
        fetchDetails();
    }, [formData]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            hotel_type: selectedHotelType,
            ...(selectedHotelType === "kiosk"
                ? { calling_hotel_type: kioskSelection }
                : {}),
            hotel_ids: [],
            all_selected: true,
            page: 0,
        }));
        setCurrentPage(1);
    }, [selectedHotelType]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            calling_hotel_type: kioskSelection,
        }));
    }, [kioskSelection]);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((prev) => ({
            ...prev,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            hotel_ids: selectedHotels,
            page: 0,
            all_selected: selectedHotels.length === 0,
        }));
    }, [selectedHotels]);

    useEffect(() => {
        if (detailStatus === "success") {
            const { data, total } = detailRes;
            setHotelData(data);
            setTotalRecords(total);
        }
    }, [detailStatus, detailRes]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleTableChange = (pagination, _, sorter) => {
        const { current, pageSize } = pagination;
        const { order, columnKey } = sorter;
        const updates = { page: current - 1, limit: pageSize };

        if (order) {
            updates.sorting = order === "ascend" ? 2 : 1;
            updates.sort_by = columnKey;
            updateSortedInfo({ columnKey, order });
        } else {
            const { sorting: _s, sort_by: _sb, ...rest } = formData;
            updateSortedInfo({});
            return setFormData({ ...rest, ...updates });
        }

        setFormData((prev) => ({ ...prev, ...updates }));
        setCurrentPage(current);
    };

    const downloadReport = async () => {
        if (hotelData.length === 0) return;
        const { page: _p, limit: _l, ...payload } = formData;
        const res = await HTTP("/dashboard/getDashboardDataReport", payload);
        downloadCSV(res, `${state?.component_name ?? "quality"}_issue_reports`);
    };

    const handleHotelClick = (hotel_id, record) => {
        navigate("/support-details-table", {
            state: {
                id: Number(hotel_id),
                filter: true,
                component_name: state?.component_name,
            },
        });
    };

    // ── Derived ───────────────────────────────────────────────────────────────

    const canDownload = DOWNLOAD_ROLES.includes(userRole);
    const isLoading = !hotelData.length && detailStatus !== "success";

    const columns = buildColumns({
        componentLabel: state?.component,
        sortedInfo,
        onHotelClick: handleHotelClick,
    });

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Card className="dashboard-container">
            <CustomHeader
                extraAfter={
                    canDownload && (
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={downloadReport}
                        >
                            Download
                        </Button>
                    )
                }
            />

            <Card className="details-card">
                {isLoading ? (
                    <Skeleton
                        active
                        title={false}
                        paragraph={{ rows: 20, width: 40 }}
                    />
                ) : (
                    <CustomTable
                        columns={columns}
                        tableData={hotelData}
                        currentPage={currentPage}
                        noOfrecords={totalRecords}
                        handleTableChange={handleTableChange}
                        rowKey="hotel_id"
                        className="dashboard-table"
                    />
                )}
            </Card>
        </Card>
    );
}

export { QualityMatricsDetails };
